import { useCallback, Dispatch, SetStateAction, useMemo } from "react";

import moment from "moment";
import { IntlShape, useIntl } from "react-intl";

import {
    useAnalyticsEvents,
    CreateUIAnalyticsEvent
} from "@atlaskit/analytics-next";
import {
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE
} from "@atlassian/onboarding-survey";
import OriginTracer from "@atlassiansox/origin-tracing";

import { ufoEventOnboardingInviteUsers } from "@/components/signup/utils/ufo-events";
import { UfoCustomTimingKey } from "@/components/signup/utils/ufo.types";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import {
    WizardCreateProjectSampleIssue,
    MadlibContentGenerationResponse,
    MadlibContentGenerationChildWorkItem,
    TaskError,
    EmailInviteRequest
} from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import {
    ProjectCreateError,
    MadlibContentGenerationError
} from "@/libraries/bxp-web-stargate-client/src/util/unsuccessful-fetch-error";
import { experiments } from "@/utils/experiment/manifest";
import { getExperimentWithExposures } from "@/utils/experiment/use-experiment";

import {
    getProductQueryParamTeamType,
    getIdentityTeamType,
    LANDING_GATE_NON_TECH_TEAM_TYPES
} from "../../../models/user-segmentation";
import { useIsSampleTasksForLandingGateNonTech } from "../../onboarding-wizard/hooks/experiment-hooks";
import { findRoleIdWithName } from "../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-fetch-project-role-ids";
import { parseJiraEditionFromLicenseInformation } from "../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/hooks/utils";
import { generateContinueUrl } from "../../onboarding-wizard/wizard/variant/jfind-1663-page-invite/utils";
import { MadlibStepContext } from "../common/steps/madlib/madlib";

import {
    confiugredIssueTypes,
    ANALYTIC_EVENT_ACTION_STARTED,
    ANALYTIC_EVENT_ACTION_FAILED,
    ANALYTIC_EVENT_ACTION_SUCCEEDED,
    PROJECT_CREATE_ANALYTIC_EVENT_NAME,
    PROJECT_POLLING_ANALYTIC_EVENT_NAME,
    MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME,
    MADLIB_FLOW,
    DEFAULT_FLOW
} from "./constants";
import { issueTypeMessages, manualIssueMessages } from "./messages";

const PROJECT_CREATE_TASK_POLLING_TIMEOUT = 30_000;

const setProjectCreateTaskIdQueryParam = (taskId: string) => {
    const url = new URL(window.location.href);
    if (url) {
        url.searchParams.set("taskId", taskId);
        window.history.replaceState({}, "", url.toString());
    }
};

export const useCreateProject = () => {
    const stargateClient = useBxpWebStargateClient();
    const { user } = useUserContext();
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { sampleTasksForLandingGateNonTechIsEnrolledInTest } =
        useIsSampleTasksForLandingGateNonTech();

    const { toDoColumnName, inProgressColumnName, doneColumnName } =
        useMemo(() => {
            const toDoColumnName = intl.formatMessage(
                issueTypeMessages.todoColumnName
            );
            const inProgressColumnName = intl.formatMessage(
                issueTypeMessages.inProgressColumnName
            );
            const doneColumnName = intl.formatMessage(
                issueTypeMessages.doneColumnName
            );

            return {
                toDoColumnName,
                inProgressColumnName,
                doneColumnName
            };
        }, [intl]);

    const createDefaultProject = useCallback(
        async ({
            context,
            setContext
        }: {
            context: MadlibStepContext;
            setContext: Dispatch<SetStateAction<MadlibStepContext>>;
        }) => {
            const {
                template = "KANBAN",
                tenantId = "",
                spaceName = "",
                teamType = ""
            } = context;

            exposeExperimentsInHoldout(context);

            try {
                const projectKey = getProjectKeyFromTeamType(teamType);

                const { taskId } = await stargateClient.postProjectCreate({
                    projectName: spaceName,
                    cloudId: tenantId,
                    template,
                    issueTypes: confiugredIssueTypes,
                    statusNames: [
                        toDoColumnName,
                        inProgressColumnName,
                        doneColumnName
                    ],
                    teamType: getProductQueryParamTeamType(teamType),
                    customDetail: {
                        enableExtraFields: true,
                        sampleDataVersion: getSampleVersion(
                            teamType,
                            template,
                            sampleTasksForLandingGateNonTechIsEnrolledInTest
                        )
                    },
                    projectKey
                });

                setProjectCreateTaskIdQueryParam(taskId);

                createAnalyticsEvent({
                    action: ANALYTIC_EVENT_ACTION_STARTED,
                    eventType: "track",
                    actionSubject: PROJECT_CREATE_ANALYTIC_EVENT_NAME,
                    attributes: {
                        template,
                        teamType,
                        tenantId,
                        flow: DEFAULT_FLOW
                    }
                }).fire();

                setContext((prevContext) => ({
                    ...prevContext,
                    taskId
                }));

                const { status, result } =
                    await stargateClient.pollTaskUntilComplete(
                        taskId,
                        tenantId,
                        PROJECT_CREATE_TASK_POLLING_TIMEOUT
                    );

                if (status === "COMPLETE") {
                    const projectId = result?.projectId;
                    setContext((prevContext) => ({
                        ...prevContext,
                        projectId
                    }));

                    createAnalyticsEvent({
                        action: ANALYTIC_EVENT_ACTION_SUCCEEDED,
                        eventType: "track",
                        actionSubject: PROJECT_CREATE_ANALYTIC_EVENT_NAME,
                        attributes: {
                            template,
                            teamType,
                            tenantId,
                            flow: DEFAULT_FLOW
                        }
                    }).fire();
                }

                if (status !== "COMPLETE") {
                    throw new Error(
                        "Project creation did not complete successfully"
                    );
                }
            } catch (error) {
                fireErrorAnalyticEvents(createAnalyticsEvent, {
                    error: error as Error,
                    template,
                    teamType,
                    tenantId,
                    flow: DEFAULT_FLOW
                });
            }
        },
        [
            stargateClient,
            toDoColumnName,
            inProgressColumnName,
            doneColumnName,
            createAnalyticsEvent,
            sampleTasksForLandingGateNonTechIsEnrolledInTest
        ]
    );

    const createMadlibProject = useCallback(
        async ({
            context,
            setContext
        }: {
            context: MadlibStepContext;
            setContext: Dispatch<SetStateAction<MadlibStepContext>>;
        }) => {
            const {
                template = "KANBAN",
                tenantId = "",
                spaceName = "",
                teamType = "",
                data = { task: "", goal: "", dueDate: new Date().getTime() }
            } = context;

            exposeExperimentsInHoldout(context);

            try {
                const response =
                    await stargateClient.postMadlibContentGeneration(tenantId, {
                        spaceName: spaceName,
                        workingOnSummary: data.task,
                        goal: data.goal,
                        teamType: getIdentityTeamType(teamType)
                    });

                createAnalyticsEvent({
                    action: ANALYTIC_EVENT_ACTION_SUCCEEDED,
                    eventType: "track",
                    actionSubject:
                        MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME,
                    attributes: {
                        template,
                        teamType,
                        tenantId,
                        flow: MADLIB_FLOW
                    }
                }).fire();

                const sampleIssues = mapMadlibResponseToSampleIssues(
                    response,
                    data.dueDate,
                    intl,
                    user["accountId"]
                );

                const projectKey = getProjectKeyFromTeamType(teamType);

                const { taskId } = await stargateClient.postProjectCreate({
                    projectName: spaceName,
                    cloudId: tenantId,
                    template,
                    issueTypes: confiugredIssueTypes,
                    statusNames: [
                        toDoColumnName,
                        inProgressColumnName,
                        doneColumnName
                    ],
                    teamType: getProductQueryParamTeamType(teamType),
                    customDetail: {
                        enableExtraFields: true,
                        sampleDataVersion: "MANUAL"
                    },
                    projectKey,
                    sampleIssues
                });

                setProjectCreateTaskIdQueryParam(taskId);

                createAnalyticsEvent({
                    action: ANALYTIC_EVENT_ACTION_STARTED,
                    eventType: "track",
                    actionSubject: PROJECT_CREATE_ANALYTIC_EVENT_NAME,
                    attributes: {
                        template,
                        teamType,
                        tenantId,
                        flow: MADLIB_FLOW
                    }
                }).fire();

                setContext((prevContext) => ({
                    ...prevContext,
                    taskId
                }));

                const { status, result } =
                    await stargateClient.pollTaskUntilComplete(
                        taskId,
                        tenantId,
                        PROJECT_CREATE_TASK_POLLING_TIMEOUT
                    );

                if (status === "COMPLETE") {
                    const projectId = result?.projectId;
                    setContext((prevContext) => ({
                        ...prevContext,
                        projectId
                    }));

                    createAnalyticsEvent({
                        action: ANALYTIC_EVENT_ACTION_SUCCEEDED,
                        eventType: "track",
                        actionSubject: PROJECT_CREATE_ANALYTIC_EVENT_NAME,
                        attributes: {
                            template,
                            teamType,
                            tenantId,
                            flow: MADLIB_FLOW
                        }
                    }).fire();
                } else {
                    throw new Error(
                        "Project creation did not complete successfully"
                    );
                }
            } catch (error) {
                fireErrorAnalyticEvents(createAnalyticsEvent, {
                    error: error as Error,
                    template,
                    teamType,
                    tenantId,
                    flow: MADLIB_FLOW
                });

                // Attempt default project creation if Content Generation or Madlib project creation fails
                if (
                    error instanceof MadlibContentGenerationError ||
                    error instanceof ProjectCreateError
                ) {
                    await createDefaultProject({ context, setContext });
                }
            }
        },
        [
            createDefaultProject,
            stargateClient,
            toDoColumnName,
            inProgressColumnName,
            doneColumnName,
            user.accountId,
            intl,
            createAnalyticsEvent
        ]
    );

    return {
        createMadlibProject,
        createDefaultProject
    };
};

export const generateSequentialDueDates = (
    endDate: number,
    numberOfIssues: number
): string[] => {
    // Generate well-spaced sequential due dates for child issues (all at midnight)
    const startDate = moment().startOf("day");
    const finalDate = moment(endDate).startOf("day");
    const totalDays = finalDate.diff(startDate, "days");

    // Divide the day range into intervals and randomly select a day within each interval
    // This ensures even distribution across the entire date range
    const intervalSizeInDays = totalDays / numberOfIssues;

    return Array.from({ length: numberOfIssues }, (_, index) => {
        // Calculate the day range for this interval
        const intervalStartDay = Math.floor(index * intervalSizeInDays);
        const intervalEndDay = Math.floor((index + 1) * intervalSizeInDays);
        // Randomly select a day within this interval (ensure at least 1 day range)
        const randomDay =
            intervalStartDay +
            Math.floor(
                Math.random() * Math.max(1, intervalEndDay - intervalStartDay)
            );
        // Return date at midnight
        return startDate.clone().add(randomDay, "days").format("YYYY-MM-DD");
    });
};

const mapMadlibResponseToSampleIssues = (
    response: MadlibContentGenerationResponse,
    dueDate: number,
    intl: IntlShape,
    accountId?: string
): WizardCreateProjectSampleIssue[] => {
    const { childWorkItems } = response.sampleIssues;

    const relativeDays = getElapsedDaysFromNow(dueDate);

    const issueDueDates = generateSequentialDueDates(
        dueDate,
        childWorkItems.length
    );
    const issueDueDateRelativeDays = issueDueDates.map((issueDueDate) =>
        getElapsedDaysFromNow(new Date(issueDueDate).getTime())
    );

    const EMPTY_DESCRIPTION = "";

    const childrenIssues: WizardCreateProjectSampleIssue[] = childWorkItems.map(
        (issue: MadlibContentGenerationChildWorkItem, index: number) => ({
            refId: `task-${index + 1}`,
            parentRefId: undefined,
            issueType: "Task",
            summary: issue.summary,
            description: EMPTY_DESCRIPTION,
            transitionId: Transition.ToDo,
            relativeDays: issueDueDateRelativeDays[index] ?? relativeDays,
            priorityId: Priority.Medium,
            assigneeId: undefined
        })
    );

    const manualIssue: WizardCreateProjectSampleIssue = {
        refId: "manual-1",
        parentRefId: undefined,
        issueType: "Task",
        summary: intl.formatMessage(manualIssueMessages.summary),
        description: intl.formatMessage(manualIssueMessages.description),
        transitionId: Transition.Done,
        relativeDays: 0,
        priorityId: Priority.Low,
        assigneeId: accountId
    };

    return childrenIssues.concat([manualIssue]);
};

export const getElapsedDaysFromNow = (timestamp: number): number => {
    const now = moment();
    const targetDate = moment(timestamp);
    return targetDate.diff(now, "days");
};

export const getDateDaysFromNow = (daysFromNow: number): number =>
    Date.now() + daysFromNow * 24 * 60 * 60 * 1000;

export const Transition = {
    ToDo: 11,
    InProgress: 21,
    Done: 31
} as const;

export type Transition = (typeof Transition)[keyof typeof Transition];

export const Priority = {
    Highest: 1,
    High: 2,
    Medium: 3,
    Low: 4,
    Lowest: 5
} as const;

export type Priority = (typeof Priority)[keyof typeof Priority];

export const getProjectKeyFromTeamType = (teamType: string): string => {
    switch (teamType) {
        case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
            return "DEV";
        case USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT:
            return "IT";
        case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
            return "DES";
        case USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE:
            return "CS";
        case USER_SEGMENTATION_TEAM_TYPE_HR:
            return "HR";
        case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
            return "MKT";
        case USER_SEGMENTATION_TEAM_TYPE_OPERATIONS:
            return "OPS";
        case USER_SEGMENTATION_TEAM_TYPE_FINANCE:
            return "FNC";
        case USER_SEGMENTATION_TEAM_TYPE_LEGAL:
            return "LGL";
        case USER_SEGMENTATION_TEAM_TYPE_SALES:
            return "SAL";
        case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
            return "PDM";
        case USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT:
            return "PJM";
        case USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE:
            return "DS";
        default:
            return "PROJ";
    }
};

export const fireErrorAnalyticEvents = (
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    {
        error,
        template,
        teamType,
        tenantId,
        flow
    }: {
        error: Error;
        template: string;
        teamType: string;
        tenantId: string;
        flow: string;
    }
) => {
    if (error instanceof MadlibContentGenerationError) {
        createAnalyticsEvent({
            action: ANALYTIC_EVENT_ACTION_FAILED,
            eventType: "track",
            actionSubject: MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME,
            attributes: {
                template,
                teamType,
                tenantId,
                flow,
                message: error.message
            }
        }).fire();
    }

    if (error instanceof ProjectCreateError) {
        createAnalyticsEvent({
            action: ANALYTIC_EVENT_ACTION_FAILED,
            eventType: "track",
            actionSubject: PROJECT_CREATE_ANALYTIC_EVENT_NAME,
            attributes: {
                template,
                teamType,
                tenantId,
                flow,
                message: error.message
            }
        }).fire();
    }

    if (error instanceof TaskError) {
        createAnalyticsEvent({
            action: ANALYTIC_EVENT_ACTION_FAILED,
            eventType: "track",
            actionSubject: PROJECT_POLLING_ANALYTIC_EVENT_NAME,
            attributes: {
                template,
                teamType,
                tenantId,
                flow,
                message: error.message
            }
        }).fire();
    }
};

/*
Gets the appropriate generic sample data version based on team type and template.
 */
// This was the targeting for GENERICV2 at launch and it's restrictive to these team types in the backend.
export const getGenericSampleVersion = (
    teamType: string,
    template: "KANBAN" | "SCRUM"
) => {
    if (
        (teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE &&
            template === "KANBAN") ||
        template === "SCRUM"
    ) {
        return "GENERICV2";
    }

    return "GENERIC";
};

export const getSampleVersion = (
    teamType: string,
    template: "KANBAN" | "SCRUM",
    isEnrolledInSpaceOnboardingExperiment = false
) => {
    if (
        isEnrolledInSpaceOnboardingExperiment &&
        LANDING_GATE_NON_TECH_TEAM_TYPES.includes(
            teamType as (typeof LANDING_GATE_NON_TECH_TEAM_TYPES)[number]
        )
    ) {
        return "SPACE_ONBOARDING";
    }

    return getGenericSampleVersion(teamType, template);
};

/**
 * Exposes shipped experiments in holdout for Jira Setup flow.
 */
export const exposeExperimentsInHoldout = (context: MadlibStepContext) => {
    // This experiment targets non-software team types with Kanban template
    if (
        context.teamType !== USER_SEGMENTATION_TEAM_TYPE_SOFTWARE &&
        context.template === "KANBAN"
    ) {
        getExperimentWithExposures(
            experiments.jfindSimplifiedListViewWithBasicTasks
        );
    }

    // This experiment targets software team type with Kanban or all team types with Scrum
    if (
        (context.teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE &&
            context.template === "KANBAN") ||
        context.template === "SCRUM"
    ) {
        getExperimentWithExposures(
            experiments.jfindBasicSampleTasksForScrumOrSoftwareKanban
        );
    }
};

export const LOADING_DISPLAY_MS = 3000;

export const onLoadingStep = async ({
    context
}: {
    context: MadlibStepContext;
    setContext: Dispatch<SetStateAction<MadlibStepContext>>;
}) => {
    const workPromise = (async () => {
        for (const val of context.promisesToResolve) {
            if (val instanceof Promise) {
                await val.catch(() => {
                    /* swallow errors */
                });
            }

            if (typeof val === "function") {
                await val().catch(() => {
                    /* swallow errors */
                });
            }
        }
    })();

    const loadingBufferPromise = new Promise<void>((resolve) =>
        setTimeout(resolve, LOADING_DISPLAY_MS)
    );

    await Promise.all([workPromise, loadingBufferPromise]);
};

export const useBulkEmailInvite = () => {
    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const postBulkEmailInvite = useCallback(
        async ({
            context
        }: {
            context: MadlibStepContext;
            setContext: Dispatch<SetStateAction<MadlibStepContext>>;
        }) => {
            try {
                const { tenantId, emailList, taskId } = context;

                // do not request invites if email list is empty
                if (!emailList || emailList.length === 0) {
                    return;
                }

                // do not request invites if projectId is not defined
                if (!taskId) {
                    console.warn(
                        "project task id is not defined, skipping invites"
                    );
                    return;
                }

                ufoEventOnboardingInviteUsers.startSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK
                );

                const {
                    result: { projectId }
                } = await stargateClient.pollTaskUntilComplete(
                    taskId,
                    tenantId,
                    PROJECT_CREATE_TASK_POLLING_TIMEOUT
                );

                ufoEventOnboardingInviteUsers.endSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK
                );

                ufoEventOnboardingInviteUsers.startSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES
                );

                // get project roles
                const roles = await stargateClient.fetchProjectRoles(
                    tenantId,
                    projectId
                );

                ufoEventOnboardingInviteUsers.endSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES
                );

                const memberRole = findRoleIdWithName(
                    ["member", "user"],
                    roles
                );
                const adminRole = findRoleIdWithName(["administrator"], roles);
                let inviteeRoleId: number | null = memberRole;
                let jiraEdition: string | undefined;

                // elevating invited user to Admin for Free Edtition requires Admin role is available
                if (adminRole) {
                    try {
                        ufoEventOnboardingInviteUsers.startSubstep(
                            UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_CHECKING_LICENSE
                        );

                        const licenseInformation =
                            await stargateClient.getLicenceInformationStatus({
                                cloudId: tenantId
                            });

                        jiraEdition =
                            parseJiraEditionFromLicenseInformation(
                                licenseInformation
                            );

                        if (jiraEdition === "free") {
                            inviteeRoleId = adminRole;
                        }

                        ufoEventOnboardingInviteUsers.endSubstep(
                            UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_CHECKING_LICENSE
                        );
                    } catch {
                        createAnalyticsEvent({
                            action: "failed",
                            actionSubject: "getLicenceInformationStatus",
                            eventType: "track",
                            attributes: {
                                tenantId: context.tenantId,
                                projectId: context.projectId,
                                template: context.template,
                                teamType: context.teamType
                            }
                        }).fire();
                    }
                }

                const continueUrl: string = generateContinueUrl(projectId);
                const atlOrigin = new OriginTracer({
                    product: "jira"
                }).encode();

                let successfulInvites = 0;
                let failedInvites = 0;

                ufoEventOnboardingInviteUsers.startSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_INVITING_USERS
                );

                const invites = emailList.map(async (email) => {
                    const inviteBody = {
                        email,
                        projectRoleIds: [inviteeRoleId],
                        continueUrl,
                        atlOrigin
                    } as EmailInviteRequest;

                    // request invites
                    return (
                        stargateClient
                            .postEmailInvite(tenantId, projectId, inviteBody)
                            // deterime how many invites succeeded for analytics
                            .then(() => {
                                successfulInvites++;
                            })
                            .catch((error: unknown) => {
                                failedInvites++;
                                createAnalyticsEvent({
                                    action: "failed",
                                    actionSubject:
                                        "onboardingWizardInviteStepInviteRequest",
                                    eventType: "operational",
                                    attributes: {
                                        error:
                                            error instanceof Error
                                                ? error.message
                                                : String(error),
                                        path: `/ex/jira/${tenantId}/rest/internal/latest/invite-user/request-access/project/${projectId}`,
                                        projectId,
                                        inviteeRoleId,
                                        jiraEdition,
                                        tenantId: context.tenantId,
                                        template: context.template,
                                        teamType: context.teamType
                                    }
                                }).fire();
                            })
                    );
                });

                const results = await Promise.allSettled(invites);

                ufoEventOnboardingInviteUsers.endSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_INVITING_USERS
                );

                createAnalyticsEvent({
                    action: "succeeded",
                    actionSubject: "bulkEmailInvite",
                    eventType: "track",
                    attributes: {
                        totalInvites: results.length,
                        successfulInvites: successfulInvites,
                        failedInvites: failedInvites,
                        projectId,
                        inviteeRoleId,
                        jiraEdition,
                        tenantId: context.tenantId,
                        template: context.template,
                        teamType: context.teamType
                    }
                }).fire();

                return;
            } catch {
                createAnalyticsEvent({
                    action: "failed",
                    actionSubject: "bulkEmailInvite",
                    eventType: "track",
                    attributes: {
                        tenantId: context.tenantId,
                        projectId: context.projectId,
                        template: context.template,
                        teamType: context.teamType
                    }
                }).fire();
            }
        },
        [stargateClient, createAnalyticsEvent]
    );

    return { postBulkEmailInvite };
};
